/**
 * Hero Section
 *
 * Landing page hero section with:
 * - Background pattern
 * - Main heading
 * - Search input with button and autocomplete dropdown
 * - Quick service tags (from popular subcategories)
 */

'use client';

import { useState, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations, useLocale } from 'next-intl';
import { Container } from '@/src/shared/ui/Container';
import { Typography } from '@/src/shared/ui/typography/Typography';
import { SearchPanel, type SearchOption } from '@/src/widgets/SearchPanel';
import { SecondaryButton } from '@/src/shared/ui/button/SecondaryButton';
import { usePopularSubcategories } from '@/src/entities/category/model/hooks/usePopularSubcategories';
import { useAllCategories } from '@/src/entities/category';
import { getLocalizedName, type Locale } from '@hummii/shared-types';
import { useSearchHistory, type SearchHistoryEntry } from '@/src/entities/category/model/hooks/useSearchHistory';

const QUICK_SERVICES_LIMIT = 5;

export function HeroSection() {
  const [searchValue, setSearchValue] = useState('');
  const [selectedSubcategorySlug, setSelectedSubcategorySlug] = useState<string | null>(null);
  const router = useRouter();
  const t = useTranslations('landing.hero');
  const locale = useLocale() as Locale;

  // Fetch all categories for autocomplete
  const { categoryTree, loading: categoriesLoading } = useAllCategories();

  // Fetch popular subcategories for quick service tags
  const { subcategories, loading: popularLoading } = usePopularSubcategories({
    limit: QUICK_SERVICES_LIMIT,
  });

  // Search history stored in localStorage
  const { history, addToHistory } = useSearchHistory();

  // Build subcategory options for autocomplete
  const subcategoryOptions = useMemo((): SearchOption[] => {
    if (!categoryTree || categoryTree.length === 0) return [];

    const options: SearchOption[] = [];

    for (const rootCategory of categoryTree) {
      if (rootCategory.children && rootCategory.children.length > 0) {
        for (const child of rootCategory.children) {
          const childName = getLocalizedName(child, locale);
          const parentName = getLocalizedName(rootCategory, locale);

          options.push({
            id: child.id,
            label: childName,
            sublabel: parentName,
            value: child.slug,
          });

          // Include nested subcategories (level 2+)
          if (child.children && child.children.length > 0) {
            for (const grandchild of child.children) {
              const grandchildName = getLocalizedName(grandchild, locale);
              options.push({
                id: grandchild.id,
                label: grandchildName,
                sublabel: `${parentName} → ${childName}`,
                value: grandchild.slug,
              });
            }
          }
        }
      }
    }

    return options;
  }, [categoryTree, locale]);

  // Lookup map: slug → SearchHistoryEntry (for saving history on autocomplete select)
  const categoryBySlug = useMemo(() => {
    const map = new Map<string, SearchHistoryEntry>();
    for (const root of categoryTree) {
      for (const child of root.children ?? []) {
        map.set(child.slug, { id: child.id, slug: child.slug, nameEn: child.nameEn, nameFr: child.nameFr, parentId: child.parentId });
        for (const gc of child.children ?? []) {
          map.set(gc.slug, { id: gc.id, slug: gc.slug, nameEn: gc.nameEn, nameFr: gc.nameFr, parentId: gc.parentId });
        }
      }
    }
    return map;
  }, [categoryTree]);

  // Handle option selection from dropdown - navigate immediately
  const handleOptionSelect = (option: SearchOption) => {
    const entry = categoryBySlug.get(option.value);
    if (entry) addToHistory(entry);
    setSelectedSubcategorySlug(option.value);
    router.push(`/${locale}/search?subcategory=${option.value}`);
  };

  // Handle search button click - navigate to search page
  const handleSearch = () => {
    if (selectedSubcategorySlug) {
      router.push(`/${locale}/search?subcategory=${selectedSubcategorySlug}`);
    } else if (searchValue.trim()) {
      // Try to find matching subcategory
      const matchingOption = subcategoryOptions.find(
        (opt) => opt.label.toLowerCase() === searchValue.toLowerCase()
      );
      if (matchingOption) {
        router.push(`/${locale}/search?subcategory=${matchingOption.value}`);
      } else {
        router.push(`/${locale}/search?q=${encodeURIComponent(searchValue)}`);
      }
    } else {
      router.push(`/${locale}/search`);
    }
  };

  // Handle quick service tag click
  const handleServiceClick = (entry: SearchHistoryEntry) => {
    addToHistory(entry);
    const localizedName = getLocalizedName(entry, locale);
    setSearchValue(localizedName);
    setSelectedSubcategorySlug(entry.slug);
    router.push(`/${locale}/search?subcategory=${entry.slug}`);
  };

  // History items come first; remaining slots filled with popular subcategories not already in history
  const displaySubcategories: SearchHistoryEntry[] = [
    ...history,
    ...subcategories.filter((s) => !history.some((h) => h.slug === s.slug)),
  ].slice(0, QUICK_SERVICES_LIMIT);

  return (
    <section className="relative">
      

      {/* Content Container - positioned to align SearchPanel with background end */}
      <Container className="relative h-[300px] z-40 pt-16 pb-8 md:pt-20 md:pb-10">
        <div className="absolute bottom-[37px] left-0 right-0 mx-auto text-center">
          {/* Main Heading */}
          <Typography
            variant="h1"
            weight="bold"
            className="mb-8 text-text-primary"
          >
            {t('title')}
          </Typography>

          {/* Search Bar - centered at ~300px from top (end of background.svg) */}
          <div className="mb-6 max-w-5xl mx-auto relative z-40">
            <SearchPanel
              value={searchValue}
              onChange={(value) => {
                setSearchValue(value);
                // Reset selected subcategory if user types manually
                if (selectedSubcategorySlug) {
                  const selectedOption = subcategoryOptions.find(
                    (opt) => opt.value === selectedSubcategorySlug
                  );
                  if (selectedOption && selectedOption.label !== value) {
                    setSelectedSubcategorySlug(null);
                  }
                }
              }}
              onSearch={handleSearch}
              placeholder={t('searchPlaceholder')}
              buttonText={t('searchButton')}
              options={subcategoryOptions}
              onOptionSelect={handleOptionSelect}
              optionsLoading={categoriesLoading}
              showAllOptions={true}
            />
          </div>

          {/* Quick Service Tags */}
          <div className="w-full overflow-x-auto scrollbar-hide">
            <div className="flex gap-3 justify-center min-w-max mx-auto">
              {popularLoading && subcategories.length === 0 ? (
                // Show skeleton loaders while loading
                <>
                  {Array.from({ length: QUICK_SERVICES_LIMIT }).map((_, index) => (
                    <div
                      key={`skeleton-${index}`}
                      className="h-9 w-32 bg-gray-200 rounded-full animate-pulse flex-shrink-0"
                    />
                  ))}
                </>
              ) : (
                // Show search history (if any) or popular subcategories
                displaySubcategories.map((item) => {
                  const localizedName = getLocalizedName(item, locale);
                  return (
                    <SecondaryButton
                      key={item.id}
                      onClick={() => handleServiceClick(item)}
                      className="whitespace-nowrap flex-shrink-0"
                    >
                      <Typography variant="bodySm" color="inherit">
                        {localizedName}
                      </Typography>
                    </SecondaryButton>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}
